// ui/modules/legal/config/ContractModuleConfig.ts
// ✅ 用這份「全文」直接覆蓋整個檔案
// ✅ 修正點：同時提供 named export 與 default export，避免 ContractReviewsPage 匯入時被 Rollup 判定「未匯出」

import type { LegalModuleConfig } from "../types";
import React from "react";
import { getContractTypeLabel } from "../../../constants/contractTypes";

function toDateLabel(v: any): string {
  if (!v) return "";
  try {
    // Firestore Timestamp
    if (typeof v === "object") {
      if (typeof (v as any).toDate === "function") {
        const d = (v as any).toDate();
        return d instanceof Date && !Number.isNaN(d.getTime()) ? d.toLocaleDateString() : String(v);
      }
      if (typeof (v as any).toMillis === "function") {
        const d = new Date((v as any).toMillis());
        return !Number.isNaN(d.getTime()) ? d.toLocaleDateString() : String(v);
      }
    }
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString();
  } catch {
    return String(v);
  }
}

function contractStatusLabel(raw: any): string {
  if (!raw) return "";
  const s = String(raw).trim();
  const u = s.toUpperCase();

  // 中文狀態
  if (s.includes("尚未生效") || s.includes("未生效") || s.includes("草稿")) return "尚未生效";
  if (s.includes("生效中") || s.includes("有效")) return "生效中";
  if (s.includes("已屆滿") || s.includes("已到期")) return "已屆滿";
  if (s.includes("已解約") || s.includes("已終止")) return "已解約";

  // 代碼狀態
  if (u === "NOT_EFFECTIVE" || u === "DRAFT") return "尚未生效";
  if (u === "ACTIVE") return "生效中";
  if (u === "EXPIRED") return "已屆滿";
  if (u === "TERMINATED") return "已解約";

  // 兼容舊流程/簽核用狀態（不強制限制，避免顯示空白）
  const map: Record<string, string> = {
    IN_REVIEW: "簽核中",
    WAITING_APPROVAL: "簽核中",
    PENDING_MANAGER: "簽核中",
    APPROVED: "已核准",
    REJECTED: "已駁回",
    RETURNED: "退回",
  };
  return map[u] || s;
}

export const contractModuleConfig: LegalModuleConfig = {
  id: "contracts",
  title: "合約系統管理",
  collection: "legal_contracts",
  searchableFields: ["title", "contractNo", "counterparty", "notes"],
  formSchema: "ContractForm",
  importMapping: "contracts",
  exportMapping: "contracts",
  canEdit: true,
  columns: [
    { key: "contractNo", label: "合約編號", width: 120, type: "text" },
    { key: "title", label: "標題", width: 200, type: "text" },
    {
      key: "contractType",
      label: "合約類型",
      width: 120,
      type: "text",
      render: (row: any) => getContractTypeLabel(row.contractType),
    },
    { key: "counterparty", label: "相對人", width: 160, type: "text" },
    {
      // ✅ 合約留存管理首頁：以「起始日（生效日）」作為主要日期欄位
      key: "startDate",
      label: "起始日",
      width: 110,
      type: "date",
      render: (row: any) =>
        toDateLabel(
          row.periodStart ||
            row.startDate ||
            row.effectiveDate ||
            row.signDate ||
            row.signedDate
        ),
    },
    {
      key: "periodEnd",
      label: "到期日",
      width: 110,
      type: "date",
      render: (row: any) => toDateLabel(row.periodEnd || row.expiryDate),
    },
    {
      key: "status",
      label: "狀態",
      width: 90,
      type: "text",
      render: (row: any) => contractStatusLabel(row.status),
    },
    {
      key: "riskLevel",
      label: "風險",
      width: 80,
      type: "text",
      render: (row: any) => {
        const color =
          row.riskLevel === "high"
            ? "var(--danger)"
            : row.riskLevel === "medium"
            ? "#d97706"
            : "var(--muted)";
        return React.createElement(
          "span",
          { style: { color } },
          row.riskLevel === "high" ? "高" : row.riskLevel === "medium" ? "中" : "低"
        );
      },
    },
  ],
};

// ✅ 兼容 default import（有些頁面/歷史版本可能改成 default 匯入）
export default contractModuleConfig;
